/**
 * exportAgentPDF.js
 * ใช้ใน AgentRegisterDetailView
 *
 * ติดตั้ง: npm install jspdf html2canvas --legacy-peer-deps
 *
 * วิธีใช้:
 *   import { ExportAgentPDFButton } from "./exportAgentPDF";
 *   <ExportAgentPDFButton form={form} files={files} />
 */

import React, { useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Button, CircularProgress } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
async function toBase64(url) {
  const res  = await fetch(url);
  const blob = await res.blob();
  
  // [FIX] เช็ค content-type ก่อน — ถ้าไม่ใช่ image ให้ return ""
  if (!blob.type.startsWith("image/")) {
    console.warn("ไม่ใช่ไฟล์รูป:", blob.type);
    return "";
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

// ── helper: render HTML → jsPDF ─────────────────────────────────────────
async function renderToPDF(htmlContent) {
  const container = document.createElement("div");
  container.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: 794px;
    background: white;
    font-family: Arial, sans-serif;
    font-size: 13px;
    color: #000;
    padding: 0;
  `;
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  const pdf      = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const margin   = 15;
  const contentW = 210 - margin * 2;

  const pages = container.querySelectorAll(".pdf-page");
  for (let i = 0; i < pages.length; i++) {
    const canvas = await html2canvas(pages[i], {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const imgH    = (canvas.height * contentW) / canvas.width;
    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "PNG", margin, margin, contentW, imgH);
  }

  document.body.removeChild(container);
  return pdf;
}

// ── helpers ─────────────────────────────────────────────────────────────
const cb = (checked) =>
  `<span style="display:inline-block;width:13px;height:13px;border:1.5px solid #000;
   text-align:center;line-height:13px;font-size:11px;margin-right:3px;vertical-align:middle;">
   ${checked ? "✓" : ""}</span>`;

const line = (label, value, labelW = "180px") =>
  `<div style="margin-bottom:7px;">
     <b>${label}</b>
     <span style="display:inline-block;min-width:${labelW};border-bottom:1px solid #000;padding-left:4px;vertical-align:bottom;">${value ?? ""}</span>
   </div>`;

const inlineField = (label, value, minW = "120px") =>
  `<b>${label}</b>
   <span style="display:inline-block;min-width:${minW};border-bottom:1px solid #000;padding-left:4px;vertical-align:bottom;margin-right:16px;">${value ?? ""}</span>`;

// ── build HTML ───────────────────────────────────────────────────────────
function buildAgentHTML(form, files, logoBase64 = "") {

  const req      = form?.requestInfo    || {};
  const basic    = form?.basicInfo      || {};
  const biz      = form?.businessType   || {};
  const alliance = form?.alliance       || {};
  const fin      = form?.financial      || {};
  const regions  = form?.regions        || [];

  const isRegion   = (r) => Array.isArray(regions) && regions.includes(r);
  const profileType = form?.profileType || {};
  const finType     = fin.type || "";

  const companyProfileFiles = files?.COMPANY_PROFILE  || [];
  const agencyAgreementFiles = files?.AGENCY_AGREEMENT || [];

  const page = `
<div class="pdf-page" style="padding:24px 32px;min-height:1050px;">

  <!-- Header -->
  <table style="width:100%;margin-bottom:16px;">
  <tr>
      <td style="vertical-align:middle;width:120px;">
        ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" style="width:100px;" />` : ""}
      </td>
      <td style="vertical-align:top;text-align:right;">
        <div style="font-weight:700;font-size:15px;">SINO Logistics Corporation PLC</div>
        <div style="font-size:11px;">1011 Supalai Grand Tower 7th Floor Unit 05, Rama 3 Road,</div>
        <div style="font-size:11px;">Chongnonsee, Yannawa, Bangkok, 10120 Thailand</div>
        <div style="font-size:11px;">บริษัท ไซโน โลจิสติกส์คอร์ปอเรชั่น จำกัด (มหาชน)</div>
        <div style="font-size:11px;">1011 อาคารศุภาลัยแกรนด์ ทาวเวอร์ ชั้น 7 ยูนิต 05</div>
          <div style="font-size:11px;">ถนนพระรามสาม แขวงช่องนนทรี เขตยานนาวา กทม. 10120</div>
      </td>
    </tr>
  </table>

  <!-- Title -->
  <div style="text-align:center;font-size:17px;font-weight:700;border:2px solid #000;
       padding:8px 0;margin-bottom:16px;letter-spacing:0.5px;">
    Account Registration Form (Agent / Consignee / Shipper)
  </div>

  <!-- Date / Code No -->
  <table style="width:100%;margin-bottom:14px;">
    <tr>
      <td style="width:50%;">${inlineField("Date :", req.date, "160px")}</td>
      <td>${inlineField("Code No:", req.codeNo, "160px")}</td>
    </tr>
  </table>

  <!-- Profile Type -->
  <div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin-bottom:12px;">
    <b>Profile Type</b> (Please tick ☑ all applicable roles):
    <div style="margin-top:8px;">
      ${cb(profileType.agent     === true || form?.profileType === "agent")}     Agent &nbsp;&nbsp;&nbsp;
      ${cb(profileType.consignee === true || form?.profileType === "consignee")} Consignee &nbsp;&nbsp;&nbsp;
      ${cb(profileType.notify    === true || form?.profileType === "notify")}    Notify &nbsp;&nbsp;&nbsp;
      ${cb(profileType.shipper   === true || form?.profileType === "shipper")}   Shipper
    </div>
  </div>

  <!-- Basic Information -->
  <div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin-bottom:12px;">
    <b style="font-size:13px;">Basic Information :</b>
    <div style="margin-top:8px;">
      ${line("Agent/Consignee Name :", basic.name, "360px")}
      <div style="margin-bottom:7px;">
        ${inlineField("Address :", basic.address, "160px")}
        ${inlineField("District :", basic.district, "100px")}
        ${inlineField("Sub-District :", basic.subdistrict, "100px")}
      </div>
      <div style="margin-bottom:7px;">
        ${inlineField("City :", basic.city, "120px")}
        ${inlineField("Postal Code :", basic.postalCode, "80px")}
        ${inlineField("Country :", basic.country, "100px")}
      </div>
      <div style="margin-bottom:7px;">
        ${inlineField("Email :", basic.email, "140px")}
        ${inlineField("Tel :", basic.tel, "100px")}
        ${inlineField("Contact Person :", basic.contactPerson, "120px")}
      </div>
    </div>

    <!-- Regions -->
    <div style="margin-top:8px;">
      <b>Regions :</b>&nbsp;&nbsp;
      ${cb(isRegion("Asia"))}Asia &nbsp;
      ${cb(isRegion("Indian Sub"))}Indian Sub &nbsp;
      ${cb(isRegion("Middle East"))}Middle East &nbsp;
      ${cb(isRegion("Oceania"))}Oceania &nbsp;
      ${cb(isRegion("Africa"))}Africa &nbsp;
      ${cb(isRegion("Europe"))}Europe
    </div>
    <div style="margin-top:5px;padding-left:56px;">
      ${cb(isRegion("Mediterranean"))}Mediterranean &nbsp;
      ${cb(isRegion("North America"))}North America &nbsp;
      ${cb(isRegion("Latin America"))}Latin America
    </div>
  </div>

  <!-- Type of Business -->
  <div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin-bottom:12px;">
    <b>Type of Business</b> (Please tick ☑ the services engaged with this party) :
    <div style="margin-top:8px;">
      ${cb(biz.seaExport)} Sea Export &nbsp;&nbsp;
      ${cb(biz.seaImport)} Sea Import &nbsp;&nbsp;
      ${cb(biz.airExport)} Air Export &nbsp;&nbsp;
      ${cb(biz.airImport)} Air Import &nbsp;&nbsp;
      ${cb(biz.others || biz.other)} Other
      <span style="border-bottom:1px solid #000;display:inline-block;min-width:120px;padding-left:4px;">${biz.other || ""}</span>
    </div>
  </div>

  <!-- Global Alliance / Network -->
  <div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin-bottom:12px;">
    <b>Global Alliance / Network :</b>
    <div style="margin-top:8px;">
      ${cb(alliance.WCA)} WCA &nbsp;&nbsp;
      ${cb(alliance.PPL)} PPL &nbsp;&nbsp;
      ${cb(alliance.X2)}  X2  &nbsp;&nbsp;
      ${cb(alliance.other)} Other
      <span style="border-bottom:1px solid #000;display:inline-block;min-width:140px;padding-left:4px;">${alliance.other || ""}</span>
    </div>
    <div style="margin-top:6px;">
      ${inlineField("Membership No :", alliance.membership, "280px")}
    </div>
  </div>

  <!-- Financial Details -->
  <div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin-bottom:12px;">
    <b>Financial Details :</b>
    <div style="margin-top:8px;">
      ${inlineField("Credit Term :", fin.creditTerm, "100px")} Days &nbsp;&nbsp;&nbsp;
      ${inlineField("Credit Limit :", fin.creditLimit, "100px")} USD
    </div>
    <div style="margin-top:8px;">
      ${cb(finType === "RECEIPT")} Receipt (This party pays us) &nbsp;&nbsp;
      ${cb(finType === "PAYMENT")} Payment (We pay for this party) &nbsp;&nbsp;
      ${cb(finType === "BOTH")}   Receipt and Payment (Both)
    </div>
  </div>

  <!-- Required Documents -->
  <div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin-bottom:20px;">
    <b>Required Documents :</b>
    <div style="margin-top:8px;">
      ${cb(companyProfileFiles.length > 0)}
      Business Registration Certificate (or Company Profile)
    </div>
    <div style="margin-top:5px;">
      ${cb(agencyAgreementFiles.length > 0)}
      Agency Agreement (if applicable)
    </div>
  </div>

  <!-- Signature block -->
  <table style="width:100%;margin-top:10px;">
    <tr>
      <td style="width:50%;padding-right:20px;">
        <b>Credit Applicant</b>
        <div style="margin-top:16px;">Signature ................................................................</div>
        <div style="margin-top:10px;">Title .........................................................................</div>
        <div style="margin-top:10px;">Date ........................................................................</div>
      </td>
      <td style="width:50%;padding-left:20px;">
        <b>Authorized Person</b>
        <div style="margin-top:16px;">Signature ................................................................</div>
        <div style="margin-top:10px;">Title .........................................................................</div>
        <div style="margin-top:10px;">Date ........................................................................</div>
      </td>
    </tr>
  </table>

</div>`;

  return page;
}

// ── Export function ──────────────────────────────────────────────────────
export async function exportAgentPDF(form, files) {
   const logoBase64 = await toBase64(process.env.PUBLIC_URL + "/sino-logo.png");
  const html     = buildAgentHTML(form, files, logoBase64);
  const pdf      = await renderToPDF(html);
  const codeNo   = form?.requestInfo?.codeNo || "new";
  const filename = `agent_register_${codeNo}_${new Date().toISOString().split("T")[0]}.pdf`;
  pdf.save(filename);
}

// ── React Button component ───────────────────────────────────────────────
export function ExportAgentPDFButton({ form, files }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      await exportAgentPDF(form, files);
    } finally {
      setLoading(false);
    }
  };
// ── แปลง image path → base64 ────────────────────────────────────────────
async function toBase64(url) {
  const res    = await fetch(url);
  const blob   = await res.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}
  return (
    <Button
      variant="outlined"
      color="error"
      startIcon={loading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? "กำลังสร้าง PDF..." : "Export PDF"}
    </Button>
  );
}
